import React from 'react';
import { ButtonVariant, type ButtonProps } from './types';
import { useButton } from './useButton';

const Button: React.FC<ButtonProps> = ({
  label,
  showIcon,
  icon,
  variant = ButtonVariant.Primary,
  buttonType = 'Round',
  size = 'Small',
  state = 'Enabled',
  className = '',
  disabled,
  type = 'button',
  onClick,
  onFocus,
  onBlur,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onMouseUp,
  style,
  customBgColor,
  customBgColorHover,
  customBorderColor,
  customTextColor,
  customFontWeight,
  children,
  ...props
}) => {
  const {
    config,
    borderRadius,
    isDisabled,
    stateStyles,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp,
    handleFocus,
    handleBlur,
  } = useButton({
    variant,
    buttonType,
    size,
    state,
    disabled,
    onFocus,
    onBlur,
    onMouseEnter,
    onMouseLeave,
    onMouseDown,
    onMouseUp,
  });

  return (
    <button
      id={label || 'button'}
      data-testid={label || 'button'}
      aria-label={label || 'button'}
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      className={`
        relative
        overflow-hidden
        inline-flex
        items-center
        justify-center
        transition-all
        duration-200
        ${isDisabled ? '' : stateStyles.backgroundColor}
        ${isDisabled ? '' : stateStyles.color}
        ${stateStyles.cursor}
        ${className}
      `}
      style={{
        paddingLeft: `${config.paddingX}px`,
        paddingRight: `${config.paddingX}px`,
        paddingTop: `${config.paddingY}px`,
        paddingBottom: `${config.paddingY}px`,
        borderRadius: `${borderRadius}px`,
        fontSize: `${config.fontSize}px`,
        lineHeight: `${config.lineHeight}px`,
        letterSpacing:
          size === 'Small' ? '0.1px' : size === 'XLarge' ? '0px' : '0.15px',
        gap: `${config.gap}px`,
        fontFamily: 'Roboto',
        fontWeight: customFontWeight || 400,
        ...(customBorderColor && {
          border: `1px solid ${customBorderColor}`,
        }),
        ...(isDisabled
          ? {
              backgroundColor: 'rgba(24, 26, 27, 0.15)',
              color: 'rgba(24, 26, 27, 0.38)',
              cursor: 'not-allowed',
            }
          : {
              ...(customBgColor && { backgroundColor: customBgColor }),
              ...(customTextColor && { color: customTextColor }),
            }),
        ...(stateStyles.isHovered &&
          customBgColorHover && {
            backgroundColor: customBgColorHover,
          }),
        ...style,
      }}
      {...props}
    >
      {/* Hover overlay - white with 8% opacity */}
      <span
        className="absolute inset-0 bg-white transition-opacity duration-200 pointer-events-none"
        style={{ opacity: stateStyles.isHovered ? 0.08 : 0 }}
      />
      {icon && showIcon !== false ? (
        <span
          className="relative inline-flex items-center justify-center shrink-0"
          style={{
            width: `${config.iconSize}px`,
            height: `${config.iconSize}px`,
          }}
        >
          {icon}
        </span>
      ) : null}
      {label ? (
        <span className="relative whitespace-nowrap">{label}</span>
      ) : null}
      {children}
    </button>
  );
};

export default Button;
